import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  IconButton,
  Chip,
  Divider,
  Stack,
  Paper,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { paymentAPI } from '../../services/api';

const MyPayments = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { order, invoiceDetails } = location.state || {};
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  // Form state for new/edit payment method
  const [paymentType, setPaymentType] = useState('CREDIT_CARD');
  const [creditCardData, setCreditCardData] = useState({
    number: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: ''
  });
  const [achData, setAchData] = useState({
    accountNumber: '',
    routingNumber: '',
    accountType: 'CHECKING',
    accountHolderName: ''
  });

  // Mock customer ID - will be replaced with actual Hyfin customer ID
  const customerId = `customer_${currentUser?.email?.replace('@', '_').replace('.', '_')}` || 'customer_demo';

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      
      // Temporarily disabled - no saved payment methods until proper Hyfin integration
      setPaymentMethods([]);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setPaymentMethods([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = () => {
    setEditingMethod(null);
    setPaymentType('CREDIT_CARD');
    setCreditCardData({
      number: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      cardholderName: ''
    });
    setAchData({
      accountNumber: '',
      routingNumber: '',
      accountType: 'CHECKING',
      accountHolderName: ''
    });
    setSaveAsDefault(paymentMethods.length === 0); // First method is default
    setDialogOpen(true);
  };

  const handleSavePaymentMethod = async () => {
    try {
      setSaving(true);
      setError(null);

      const paymentMethodData = {
        type: paymentType,
        ...(paymentType === 'CREDIT_CARD' ? {
          creditCard: {
            number: creditCardData.number.replace(/\s/g, ''),
            expiryMonth: creditCardData.expiryMonth,
            expiryYear: creditCardData.expiryYear,
            cvv: creditCardData.cvv,
            cardholderName: creditCardData.cardholderName
          }
        } : {
          ach: {
            accountNumber: achData.accountNumber,
            routingNumber: achData.routingNumber,
            accountType: achData.accountType,
            accountHolderName: achData.accountHolderName
          }
        })
      };

      // Save payment method to Hyfin
      const result = await paymentAPI.savePaymentMethod(customerId, {
        ...paymentMethodData,
        isDefault: paymentMethods.length === 0 || saveAsDefault
      });

      if (result.success) {
        // Reload payment methods to get updated list from Hyfin
        fetchPaymentMethods();
        setDialogOpen(false);
        console.log('Payment method saved to Hyfin successfully.');
      } else {
        throw new Error(result.error || 'Failed to save payment method to Hyfin');
      }
      
    } catch (err) {
      console.error('Error saving payment method:', err);
      setError(err.message || 'Failed to save payment method');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePaymentMethod = async (methodId) => {
    if (window.confirm('Are you sure you want to delete this payment method?')) {
      try {
        // TODO: Implement Hyfin delete payment method API
        // For now, just remove from display and reload
        setPaymentMethods(paymentMethods.filter(method => method.id !== methodId));
        console.log('Payment method deleted locally. TODO: Implement Hyfin delete API.');
      } catch (err) {
        console.error('Error deleting payment method:', err);
        setError('Failed to delete payment method');
      }
    }
  };

  const validateForm = () => {
    if (paymentType === 'CREDIT_CARD') {
      return creditCardData.number && creditCardData.expiryMonth && 
             creditCardData.expiryYear && creditCardData.cvv && creditCardData.cardholderName;
    } else {
      return achData.accountNumber && achData.routingNumber && achData.accountHolderName;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        mb: 3,
        mt: { xs: '50px', md: 0 }, // push down on mobile to sit below hamburger
        background: 'linear-gradient(135deg, #039582 0%, #027A6B 100%)',
        p: 3,
        borderRadius: 2,
        color: 'white',
        pl: { xs: '16px', md: 3 },
        transition: 'margin 0.2s ease'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
          My Payment Methods
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        Manage your saved payment methods for faster checkout. All payment information is securely stored with Hyfin.
        </Typography>
      </Box>

      {order && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f0fdfa', border: '1px solid #99f6e4' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Pay Order {order.orderNumber || order.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance: ${invoiceDetails?.balance?.toFixed(2)} {invoiceDetails?.earlyPaymentEligible && `(10% early discount eligible)`}
              </Typography>
            </Box>
            <Button variant="contained" onClick={() => navigate('/client/pay-invoice', { state: { order, invoiceDetails } })} sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}>
              Continue to Payment
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Security Notice */}
      <Alert 
        severity="info" 
        icon={<SecurityIcon />} 
        sx={{ mb: 4 }}
      >
        <Typography variant="body2">
          <strong>Secure & Private:</strong> Your payment information is encrypted and stored by Hyfin, 
          our PCI-compliant payment processor. We never store your credit card or bank account details on our servers.
        </Typography>
      </Alert>

      {/* Add Payment Method Button */}
      <Box sx={{ mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPaymentMethod}
          sx={{
            bgcolor: '#0d9488',
            '&:hover': { bgcolor: '#0f766e' },
            px: 3,
            py: 1.5,
            fontWeight: 600
          }}
        >
          Add New Payment Method
        </Button>
      </Box>

      {/* Payment Methods List */}
      <Grid container spacing={3}>
        {paymentMethods.map((method) => (
          <Grid item xs={12} md={6} key={method.id}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                border: method.isDefault ? '2px solid #0d9488' : '1px solid #e5e7eb',
                position: 'relative'
              }}
            >
              {method.isDefault && (
                <Chip
                  label="Default"
                  size="small"
                  color="success"
                  sx={{ position: 'absolute', top: 12, right: 12 }}
                />
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {method.type === 'CREDIT_CARD' ? (
                  <CreditCardIcon sx={{ mr: 2, fontSize: 32, color: '#0d9488' }} />
                ) : (
                  <BankIcon sx={{ mr: 2, fontSize: 32, color: '#0d9488' }} />
                )}
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {method.type === 'CREDIT_CARD' ? 'Credit Card' : 'Bank Account'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {method.displayName}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Added on {new Date(method.createdAt).toLocaleDateString()}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <IconButton 
                  size="small" 
                  color="primary"
                  onClick={() => {/* TODO: Edit functionality */}}
                >
                  <EditIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  color="error"
                  onClick={() => handleDeletePaymentMethod(method.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
        
        {paymentMethods.length === 0 && !loading && (
          <Grid item xs={12}>
            <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#f8fafc' }}>
              <CreditCardIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No Payment Methods Added
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Add a credit card or bank account to make payments faster and easier.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddPaymentMethod}
                sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
              >
                Add Payment Method
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Payment Method Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMethod ? 'Edit Payment Method' : 'Add New Payment Method'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Payment Type Selection */}
          <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
            <FormLabel component="legend">Payment Type</FormLabel>
            <RadioGroup
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              row
            >
              <FormControlLabel value="CREDIT_CARD" control={<Radio />} label="Credit Card" />
              <FormControlLabel value="ACH" control={<Radio />} label="Bank Account" />
            </RadioGroup>
          </FormControl>

          {/* Credit Card Form */}
          {paymentType === 'CREDIT_CARD' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cardholder Name"
                  value={creditCardData.cardholderName}
                  onChange={(e) => setCreditCardData({...creditCardData, cardholderName: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Card Number"
                  value={creditCardData.number}
                  onChange={(e) => setCreditCardData({...creditCardData, number: e.target.value})}
                  placeholder="1234 5678 9012 3456"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Month"
                  value={creditCardData.expiryMonth}
                  onChange={(e) => setCreditCardData({...creditCardData, expiryMonth: e.target.value})}
                  placeholder="MM"
                  inputProps={{ maxLength: 2 }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Expiry Year"
                  value={creditCardData.expiryYear}
                  onChange={(e) => setCreditCardData({...creditCardData, expiryYear: e.target.value})}
                  placeholder="YYYY"
                  inputProps={{ maxLength: 4 }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="CVV"
                  value={creditCardData.cvv}
                  onChange={(e) => setCreditCardData({...creditCardData, cvv: e.target.value})}
                  placeholder="123"
                  required
                />
              </Grid>
            </Grid>
          )}

          {/* ACH Form */}
          {paymentType === 'ACH' && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Holder Name"
                  value={achData.accountHolderName}
                  onChange={(e) => setAchData({...achData, accountHolderName: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Routing Number"
                  value={achData.routingNumber}
                  onChange={(e) => setAchData({...achData, routingNumber: e.target.value})}
                  placeholder="123456789"
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={achData.accountNumber}
                  onChange={(e) => setAchData({...achData, accountNumber: e.target.value})}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel component="legend">Account Type</FormLabel>
                  <RadioGroup
                    value={achData.accountType}
                    onChange={(e) => setAchData({...achData, accountType: e.target.value})}
                    row
                  >
                    <FormControlLabel value="CHECKING" control={<Radio />} label="Checking" />
                    <FormControlLabel value="SAVINGS" control={<Radio />} label="Savings" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {/* Set as Default Checkbox */}
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                />
              }
              label="Set as default payment method"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePaymentMethod}
            variant="contained"
            disabled={!validateForm() || saving}
            sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
          >
            {saving ? 'Saving...' : 'Save Payment Method'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyPayments;
